import React, { useEffect, useState } from "react";
import { adminAxios } from "../api";
import { toast } from "sonner";
import { Loader2, Search, X } from "lucide-react";
import { Input } from "../../components/ui/input";

const ORDER_STATUSES = ["pending", "confirmed", "processing", "shipped", "delivered", "cancelled"];
const payColor = (s) => ({ paid: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-700", failed: "bg-red-100 text-red-700", created: "bg-slate-100 text-slate-600", cancelled: "bg-slate-200 text-slate-600", refunded: "bg-violet-100 text-violet-700" }[s] || "bg-slate-100 text-slate-600");

const OrdersAdmin = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {}; if (q) params.q = q; if (status) params.status = status;
      setOrders((await adminAxios.get("/orders", { params })).data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const cur = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
  const updateStatus = async (o, s) => {
    try { const r = await adminAxios.patch(`/orders/${o.id}/status`, { status: s }); toast.success("Status updated"); setDetail(r.data); load(); }
    catch (e) { toast.error("Update failed"); }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-slate-900 mb-1">Orders</h1>
      <p className="text-slate-500 mb-6">All customer orders</p>
      <div className="flex flex-wrap gap-3 mb-4">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ref / name / phone" className="pl-9 w-64" />
        </form>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-slate-200 rounded-md px-3 text-sm bg-white">
          <option value="">All statuses</option>
          {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-slate-400 animate-spin" /></div> : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr>
              <th className="px-4 py-3 font-medium">Ref</th><th className="px-4 py-3 font-medium">Customer</th><th className="px-4 py-3 font-medium">Amount</th><th className="px-4 py-3 font-medium">Payment</th><th className="px-4 py-3 font-medium">Order Status</th><th className="px-4 py-3 font-medium">Date</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {orders.map((o) => (
                <tr key={o.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(o)}>
                  <td className="px-4 py-3 font-medium text-slate-800">{o.order_ref}</td>
                  <td className="px-4 py-3">{o.customer?.name}<div className="text-xs text-slate-400">{o.customer?.phone}</div></td>
                  <td className="px-4 py-3">{cur(o.amounts?.total)}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs ${payColor(o.payment?.status)}`}>{(o.payment?.method||"").toUpperCase()} · {o.payment?.status}</span></td>
                  <td className="px-4 py-3"><span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">{o.status}</span></td>
                  <td className="px-4 py-3 text-slate-500">{new Date(o.created_at).toLocaleDateString("en-IN")}</td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-400">No orders found</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setDetail(null)}>
          <div className="w-full max-w-md bg-white h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="border-b px-6 py-4 flex items-center justify-between"><h2 className="font-semibold">Order {detail.order_ref}</h2><button onClick={() => setDetail(null)} className="p-2 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button></div>
            <div className="p-6 space-y-4 text-sm">
              <Section title="Customer">
                <div>{detail.customer?.name}</div><div className="text-slate-500">{detail.customer?.phone}</div><div className="text-slate-500">{detail.customer?.email}</div>
              </Section>
              <Section title="Delivery Address">
                <div className="text-slate-600">{detail.address?.line}, {detail.address?.city}, {detail.address?.state} - {detail.address?.pincode}</div>
              </Section>
              <Section title="Items">
                {(detail.items || []).map((it, i) => (<div key={i} className="flex justify-between"><span>{it.name}{it.variant_label ? ` · ${it.variant_label}` : ""} × {it.quantity}</span><span>{cur(it.line_total)}</span></div>))}
                <div className="flex justify-between text-slate-500 mt-1"><span>GST</span><span>{cur(detail.amounts?.tax)}</span></div>
                <div className="flex justify-between font-semibold border-t mt-1 pt-1"><span>Total</span><span>{cur(detail.amounts?.total)}</span></div>
              </Section>
              <Section title="Payment">
                <div>Method: {(detail.payment?.method || "").toUpperCase()}</div>
                <div>Status: {detail.payment?.status}</div>
                {detail.payment?.gateway_payment_id && <div className="text-slate-500 break-all">Payment ID: {detail.payment.gateway_payment_id}</div>}
              </Section>
              <Section title="Update Order Status">
                <select value={detail.status} onChange={(e) => updateStatus(detail, e.target.value)} className="w-full border border-slate-200 rounded-md px-3 py-2 bg-white">
                  {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="bg-slate-50 rounded-lg p-4"><div className="text-xs uppercase tracking-wide text-slate-400 mb-2">{title}</div>{children}</div>
);

export default OrdersAdmin;
